// Copyright (c) Bentley Systems
import { useEffect, useState } from "react";
import { DEFAULT_COLOR } from "../Helpers/constants";
import { resolveColorValue } from "../Helpers/color-utils";

export type UseColorPickerOptions = {
  initialColor?: string;
  required?: boolean;
  hideNoneButton?: boolean;
  onChange?: (color: string | null) => void;
};

/**
 * Custom hook for managing color picker state
 *
 * @param options - Configuration options for the color picker
 * @returns Color picker state and handlers
 *
 * @example
 * ```tsx
 * const { colorValue, handleColorChange } = useColorPicker({
 *   initialColor: "#50CE67",
 *   required: true,
 *   onChange: (color) => console.log(color)
 * });
 * ```
 */
export function useColorPicker({
  initialColor = DEFAULT_COLOR,
  required = false,
  onChange,
}: UseColorPickerOptions) {
  const [colorValue, setColorValue] = useState<string>(
    resolveColorValue(initialColor) as string,
  );

  useEffect(() => {
    setColorValue(resolveColorValue(initialColor) as string);
  }, [initialColor]);

  const handleColorChange = (newColor: string | null) => {
    const finalColor = resolveColorValue(newColor, !required) as string;
    setColorValue(finalColor);
    onChange?.(newColor);
  };

  return {
    colorValue,
    setColorValue,
    handleColorChange,
  };
}
